import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  BookOpen,
  Plus,
  Edit,
  Trash2,
  Target,
  Users,
  Filter,
  Search,
  Archive,
} from "lucide-react";
import {
  getCourses,
  createCourse,
  updateCourse,
  deleteCourse,
  getCourseStats,
} from "../../services/courses";
import { getDepartments } from "../../services/departments";
import {
  Course,
  CreateCourseRequest,
  UpdateCourseRequest,
  CourseFilter,
} from "../../types/course";
import CourseForm from "../../components/forms/CourseForm";
import Pagination from "../../components/common/Pagination";
import { useHorizontalDragScroll } from "../../hooks/useHorizontalDragScroll";

const AdminCourses: React.FC = () => {
  const queryClient = useQueryClient();
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState<
    "all" | "Begginner" | "Intermediate" | "Advanced"
  >("all");
  const [categoryFilter, setCategoryFilter] = useState<
    "all" | "core" | "elective" | "practical" | "theory"
  >("all");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("active");
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Handle Enter key press to trigger search
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setCurrentPage(1);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Modal states
  const [isCourseFormOpen, setIsCourseFormOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);

  // Build filters object
  const filters: CourseFilter = {
    page: currentPage,
    limit: pageSize,
    search: searchTerm || undefined,
    level: levelFilter === "all" ? undefined : levelFilter,
    category: categoryFilter === "all" ? undefined : categoryFilter,
    departmentId: departmentFilter === "all" ? undefined : departmentFilter,
    isActive: statusFilter === "all" ? undefined : statusFilter === "active",
  };

  // Fetch departments for filter dropdown
  const { data: departmentsResponse } = useQuery({
    queryKey: ["departments"],
    queryFn: () => getDepartments(1, 100, undefined, undefined, true),
  });

  // Fetch courses data
  const {
    data: coursesResponse,
    isLoading: coursesLoading,
    error: coursesError,
  } = useQuery({
    queryKey: ["courses", filters, pageSize],
    queryFn: () => getCourses(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch course statistics
  const { data: statsResponse } = useQuery({
    queryKey: ["courseStats"],
    queryFn: () => getCourseStats(),
  });

  const courses = coursesResponse?.data || [];
  const stats = statsResponse?.data;

  // Create course mutation
  const createCourseMutation = useMutation({
    mutationFn: createCourse,
    onSuccess: () => {
      toast.success("Course created successfully");
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      queryClient.invalidateQueries({ queryKey: ["courseStats"] });
      setIsCourseFormOpen(false);
      setEditingCourse(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to create course");
    },
  });

  // Update course mutation
  const updateCourseMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCourseRequest }) =>
      updateCourse(id, data),
    onSuccess: () => {
      toast.success("Course updated successfully");
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      queryClient.invalidateQueries({ queryKey: ["courseStats"] });
      setIsCourseFormOpen(false);
      setEditingCourse(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to update course");
    },
  });

  // Delete course mutation
  const deleteCourseMutation = useMutation({
    mutationFn: deleteCourse,
    onSuccess: () => {
      toast.success("Course deactivated successfully");
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      queryClient.invalidateQueries({ queryKey: ["courseStats"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to delete course");
    },
  });

  const handleCreateCourse = () => {
    setEditingCourse(null);
    setIsCourseFormOpen(true);
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    setIsCourseFormOpen(true);
  };

  const handleDeleteCourse = (courseId: string) => {
    if (
      window.confirm(
        "Are you sure you want to deactivate this course?\n\nThe course will be hidden from active lists but can be reactivated later by viewing 'All Status' or 'Inactive' courses.",
      )
    ) {
      deleteCourseMutation.mutate(courseId);
    }
  };

  const handleFormSubmit = async (
    data: CreateCourseRequest | UpdateCourseRequest,
  ) => {
    if (editingCourse) {
      updateCourseMutation.mutate({
        id: editingCourse._id,
        data: data as UpdateCourseRequest,
      });
    } else {
      createCourseMutation.mutate(data as CreateCourseRequest);
    }
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setLevelFilter("all");
    setCategoryFilter("all");
    setDepartmentFilter("all");
    setStatusFilter("active");
    setCurrentPage(1);
  };

  if (coursesError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading courses
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>Unable to load courses. Please try refreshing the page.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Courses</h1>
              <p className="text-gray-600 mt-2">
                Manage academic courses, curriculum, and learning objectives
              </p>
            </div>
            <button
              onClick={handleCreateCourse}
              disabled={createCourseMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Course
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        {coursesLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-white p-6 rounded-lg border border-gray-200"
              >
                <div className="flex items-center">
                  <div className="p-3 bg-gray-100 rounded-lg animate-pulse">
                    <div className="h-6 w-6 bg-gray-300 rounded"></div>
                  </div>
                  <div className="ml-4">
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse mb-2"></div>
                    <div className="h-8 w-12 bg-gray-300 rounded animate-pulse"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <BookOpen className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Total Courses
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats?.totalCourses || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Target className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Active Courses
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats?.activeCourses || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="p-3 bg-red-100 rounded-lg">
                  <Archive className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Inactive Courses
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats?.inactiveCourses || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Filter className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Avg Credits
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats?.averageCredits?.toFixed(1) || "0.0"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search courses..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
              />
            </div>

            {/* Level Filter */}
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={levelFilter}
              onChange={(e) => {
                setLevelFilter(
                  e.target.value as
                    | "all"
                    | "Begginner"
                    | "Intermediate"
                    | "Advanced",
                );
                setCurrentPage(1);
              }}
            >
              <option value="all">All Levels</option>
              <option value="Begginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
            </select>

            {/* Category Filter */}
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(
                  e.target.value as
                    | "all"
                    | "core"
                    | "elective"
                    | "practical"
                    | "theory",
                );
                setCurrentPage(1);
              }}
            >
              <option value="all">All Categories</option>
              <option value="core">Core</option>
              <option value="elective">Elective</option>
              <option value="practical">Practical</option>
              <option value="theory">Theory</option>
            </select>

            {/* Status Filter */}
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(
                  e.target.value as "all" | "active" | "inactive",
                );
                setCurrentPage(1);
              }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            {/* Department Filter */}
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={departmentFilter}
              onChange={(e) => {
                setDepartmentFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Departments</option>
              {departmentsResponse?.data?.map((dept: any) => (
                <option key={dept._id} value={dept._id}>
                  {dept.name} ({dept.code})
                </option>
              ))}
            </select>

            {/* Reset Filters */}
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Courses Table */}
        <div className="bg-white rounded-lg border border-gray-200">
          {coursesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading courses...</span>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No courses found
              </h3>
              <p className="text-gray-600 mb-4">
                Get started by creating your first course.
              </p>
              <button
                onClick={handleCreateCourse}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Course
              </button>
            </div>
          ) : (
            <>
              <div ref={tableScrollRef} className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Course
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Department
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Level & Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {courses.map((course) => (
                      <tr
                        key={course.id || course._id}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <BookOpen className="h-5 w-5 text-blue-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {course.fullName || course.name}({course.level})
                              </div>
                              <div className="text-sm text-gray-500 max-w-xs truncate">
                                {course.description || "No description"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {(course as any).departmentId?.name ||
                              "Not assigned"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {(course as any).departmentId?.code || ""}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 mr-2">
                              {course.level}
                            </span>
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                              {course.category}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              course.isActive
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {course.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditCourse(course)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded"
                              title="Edit Course"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteCourse(course.id || course._id)
                              }
                              disabled={deleteCourseMutation.isPending}
                              className="text-red-600 hover:text-red-900 p-1 rounded disabled:opacity-50"
                              title="Deactivate Course"
                            >
                              <Archive className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {coursesResponse?.pagination && (
                <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={coursesResponse.pagination.totalPages}
                    onPageChange={setCurrentPage}
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Course Form Modal */}
      {isCourseFormOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CourseForm
              course={editingCourse || undefined}
              onSubmit={handleFormSubmit}
              onClose={() => {
                setIsCourseFormOpen(false);
                setEditingCourse(null);
              }}
              loading={
                createCourseMutation.isPending || updateCourseMutation.isPending
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCourses;
